import { useState } from 'react';
import AddRounded from '@mui/icons-material/AddRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import { Box, IconButton, InputBase, Stack, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { calcTieredCost } from '../utils/tier-pricing.util';
import { monetaryFormat } from '@/utils/monetary-format';
import type { MappedFeature } from '../utils/map-graphql-to-calculator.util';
import { clampNumericAddOn } from '../utils/clamp-addon-value.util';

interface AddOnRowProps {
    feature: MappedFeature;
    extraQty: number;
    currency?: { symbol: string } | null;
    onChange: (code: string, qty: number) => void;
    disabled?: boolean;
}

export const AddOnRow = ({ feature, extraQty, currency, onChange, disabled }: AddOnRowProps) => {
    const { t } = useTranslation();
    const step = feature.addOnStep ?? 1;
    const absoluteMax = feature.addOnMax ?? 1000000;
    const tiers = feature.addOnTiers;
    const pricePerUnit = feature.addOnPricePerUnit ?? 0;

    const baseQty = feature.value;
    const max = Math.max(0, absoluteMax - baseQty);
    const extraCost = tiers?.length ? calcTieredCost(extraQty, tiers, baseQty) : extraQty * pricePerUnit;
    // When no extra units are selected, derive the unit price from the tier that
    // baseQty + step falls into — so users see the price they'd actually pay.
    const unitPrice = tiers?.length
        ? extraQty > 0
            ? extraCost / extraQty
            : calcTieredCost(step, tiers, baseQty) / step
        : pricePerUnit;

    const [inputValue, setInputValue] = useState('');
    const [isFocused, setIsFocused] = useState(false);

    const commitValue = (raw: string) => {
        const final = clampNumericAddOn(parseInt(raw, 10), feature);
        onChange(feature.code, final);
        setInputValue(String(final));
    };

    const handleDecrement = () => onChange(feature.code, Math.max(0, extraQty - step));
    const handleIncrement = () => onChange(feature.code, Math.min(max, extraQty + step));

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr auto', md: '1fr 136px 140px' },
                gridTemplateRows: { xs: 'auto auto', md: '1fr' },
                alignItems: 'center',
                py: 1.5,
                px: 2,
                borderRadius: 2,
                bgcolor: 'action.hover',
                gap: { xs: 1, md: 2 },
            }}
        >
            {/* Label + base limit */}
            <Box sx={{ minWidth: 0, gridColumn: { xs: '1 / -1', md: 'auto' } }}>
                <Typography variant="body2" fontWeight={600}>
                    {t(feature.label)}
                </Typography>
                {feature.description && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        {t(feature.description)}
                    </Typography>
                )}
                <Typography variant="caption" color={feature.value === 0 ? 'text.secondary' : 'success.main'}>
                    {feature.value === 0
                        ? t('billing.calculator.addon.not-included-in-plan')
                        : t('billing.calculator.addon.included-value', { value: feature.displayValue })}
                </Typography>
            </Box>

            {/* Stepper */}
            <Stack
                direction="row"
                alignItems="center"
                justifyContent={{ xs: 'flex-start', md: 'center' }}
                spacing={0.5}
            >
                <Tooltip title={t('billing.calculator.addon.stepper.remove')}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleDecrement}
                            disabled={extraQty === 0 || !!disabled}
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                            <RemoveRounded fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>

                <InputBase
                    value={isFocused ? inputValue : extraQty}
                    disabled={!!disabled}
                    onFocus={() => {
                        setIsFocused(true);
                        setInputValue(String(extraQty));
                    }}
                    onChange={(e) => setInputValue(e.target.value)}
                    onBlur={(e) => {
                        setIsFocused(false);
                        commitValue(e.target.value);
                    }}
                    onKeyDown={(e) => e.key === 'Enter' && commitValue((e.target as HTMLInputElement).value)}
                    sx={{
                        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                            display: 'none',
                        },
                        '& input[type=number]': { MozAppearance: 'textfield' },
                    }}
                    inputProps={{
                        style: {
                            textAlign: 'center',
                            width: 52,
                            padding: '2px 0',
                            fontSize: '0.875rem',
                            fontWeight: 600,
                        },
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
                />

                <Tooltip title={t('billing.calculator.addon.stepper.add')}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={handleIncrement}
                            disabled={extraQty >= max || !!disabled}
                            sx={{ border: '1px solid', borderColor: 'divider' }}
                        >
                            <AddRounded fontSize="inherit" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Stack>

            {/* Price per unit + subtotal */}
            <Box sx={{ textAlign: 'right' }}>
                {extraQty > 0 ? (
                    <>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {t('billing.calculator.addon.upgrade-cost')}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} color="primary.main">
                            +{monetaryFormat(extraCost, currency)}
                            {t('billing.calculator.per-month')}
                        </Typography>
                    </>
                ) : (
                    <Typography variant="body2" color="text.disabled">
                        +{monetaryFormat(unitPrice * step, currency)}
                        {t('billing.calculator.per-month')}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};
